/**
 * megaLevelStore — the trainer's Mega Levels per Mega form, and the ONE Mega
 * active right now (2026-09-03 owner ask). Device-local, like the trainer's
 * stardust ledger; a Mega Level is the trainer's own statement, set from
 * the species page, the My Pokémon page or the Mega Evolutions page.
 *
 * ACTIVE MEGA. Activating records the form, the level it was evolved at
 * (any level: the Pokémon they evolved may not be their highest) and an
 * eight-hour expiry. Reads auto-expire, so nothing has to run a timer; the
 * hooks tick once a minute so a countdown moves and the card leaves on
 * time. Activating also raises the form's stored level to at least the
 * chosen one, since evolving at High proves they have High.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import { MEGA_ACTIVE_MS, megaFormByKey, megaLevelInfo, type MegaLevel, type MegaFormRef } from "./megaLevels";

const LEVELS_KEY = "nexus.megaLevels.v1";
const ACTIVE_KEY = "nexus.activeMega.v1";
export const MEGA_STORE_EVENT = "nexus:mega-store";

export interface MegaLevelEntry {
  level: MegaLevel;
  updatedAt: number;
  /** Total Mega Evolutions of this form, when a page said so or activations counted them. */
  evolutions?: number;
  /** Fraction toward the next level the page's circles showed (0..1), when no count is known. */
  progressToNext?: number;
  /** When the rest period after the last Mega Evolution ends (epoch ms). */
  restEndsAt?: number;
  /** The last activation this app knows of (epoch ms). */
  lastActivatedAt?: number;
}

export interface ActiveMega {
  formKey: string;
  level: MegaLevel;
  activatedAt: number;
  expiresAt: number;
}

type LevelsShape = Record<string, MegaLevelEntry>;

let levelsCache: LevelsShape | null = null;
let activeCache: ActiveMega | null | undefined;

function isLevel(v: unknown): v is MegaLevel {
  return v === 1 || v === 2 || v === 3 || v === 4;
}

function readLevels(): LevelsShape {
  if (levelsCache) return levelsCache;
  try {
    if (typeof window === "undefined") return (levelsCache = {});
    const raw = window.localStorage.getItem(LEVELS_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, Partial<MegaLevelEntry>>) : {};
    const out: LevelsShape = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (!v || !isLevel(v.level)) continue;
      const e: MegaLevelEntry = { level: v.level, updatedAt: v.updatedAt ?? 0 };
      if (typeof v.evolutions === "number" && v.evolutions >= 0) e.evolutions = v.evolutions;
      if (typeof v.progressToNext === "number") e.progressToNext = Math.max(0, Math.min(1, v.progressToNext));
      if (typeof v.restEndsAt === "number") e.restEndsAt = v.restEndsAt;
      if (typeof v.lastActivatedAt === "number") e.lastActivatedAt = v.lastActivatedAt;
      out[k] = e;
    }
    return (levelsCache = out);
  } catch {
    return (levelsCache = {});
  }
}

function writeLevels(next: LevelsShape): void {
  levelsCache = next;
  try {
    window.localStorage.setItem(LEVELS_KEY, JSON.stringify(next));
  } catch {
    /* a tiny blob; losing it is not critical */
  }
  emit();
}

function readActive(): ActiveMega | null {
  if (activeCache !== undefined) return activeCache;
  try {
    if (typeof window === "undefined") return (activeCache = null);
    const raw = window.localStorage.getItem(ACTIVE_KEY);
    if (!raw) return (activeCache = null);
    const p = JSON.parse(raw) as Partial<ActiveMega>;
    if (typeof p.formKey !== "string" || !isLevel(p.level) || typeof p.expiresAt !== "number") {
      return (activeCache = null);
    }
    return (activeCache = {
      formKey: p.formKey,
      level: p.level,
      activatedAt: typeof p.activatedAt === "number" ? p.activatedAt : p.expiresAt - MEGA_ACTIVE_MS,
      expiresAt: p.expiresAt,
    });
  } catch {
    return (activeCache = null);
  }
}

function writeActive(next: ActiveMega | null): void {
  activeCache = next;
  try {
    if (next) window.localStorage.setItem(ACTIVE_KEY, JSON.stringify(next));
    else window.localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* fine */
  }
  emit();
}

function emit(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(MEGA_STORE_EVENT));
}

/* ── Levels ──────────────────────────────────────────────────────────────── */

export function getMegaLevel(formKey: string): MegaLevel | null {
  return readLevels()[formKey]?.level ?? null;
}

export function getAllMegaLevels(): Readonly<LevelsShape> {
  return readLevels();
}

/**
 * Set (or clear with null) the trainer's level for a Mega form. A new level
 * keeps what else is known about the form (rest, count) unless the level
 * is cleared.
 */
export function setMegaLevel(formKey: string, level: MegaLevel | null): void {
  const cur = readLevels();
  const next = { ...cur };
  if (level == null) delete next[formKey];
  else {
    const prev = cur[formKey];
    next[formKey] = { ...(prev ?? {}), level, updatedAt: Date.now() };
    // A hand-set level below the counted one invalidates the count: the
    // trainer is talking about a different Pokémon of the form.
    if (prev && prev.level !== level) {
      delete next[formKey].evolutions;
      delete next[formKey].progressToNext;
    }
  }
  writeLevels(next);
}

/**
 * What a scanned page said about a form (2026-09-03): the level (raise
 * only, a page cannot prove a lower level), the count toward the next
 * level, the circles' fraction, and the rest period left. Creates the
 * entry when the page shows Base or above.
 */
export function noteMegaScan(
  formKey: string,
  read: {
    level?: MegaLevel | null;
    evolutions?: number | null;
    progressToNext?: number | null;
    restEndsAt?: number | null;
  },
  now = Date.now(),
): MegaLevelEntry | null {
  const cur = readLevels();
  const prev = cur[formKey];
  const level = read.level ?? prev?.level ?? null;
  if (level == null) return null;
  const entry: MegaLevelEntry = {
    ...(prev ?? {}),
    level: prev && prev.level > level ? prev.level : level,
    updatedAt: now,
  };
  if (read.evolutions != null) {
    entry.evolutions = Math.max(read.evolutions, prev?.evolutions ?? 0);
    delete entry.progressToNext;
  } else if (read.progressToNext != null && entry.evolutions == null) {
    entry.progressToNext = Math.max(0, Math.min(1, read.progressToNext));
  }
  if (read.restEndsAt != null) entry.restEndsAt = read.restEndsAt;
  writeLevels({ ...cur, [formKey]: entry });
  return entry;
}

/** Whether the form's rest period is still running at `now`. */
export function megaRestRemainingMs(entry: MegaLevelEntry | undefined, now = Date.now()): number {
  if (!entry?.restEndsAt) return 0;
  return Math.max(0, entry.restEndsAt - now);
}

/* ── Active Mega ─────────────────────────────────────────────────────────── */

/** The Mega active right now, or null once its eight hours are up. */
export function getActiveMega(now = Date.now()): ActiveMega | null {
  const a = readActive();
  if (!a) return null;
  if (a.expiresAt <= now) {
    writeActive(null);
    return null;
  }
  return a;
}

/**
 * Mega Evolve a form at a level for the next eight hours. The form's stored
 * level rises to at least this level.
 */
export function activateMega(formKey: string, level: MegaLevel, now = Date.now()): ActiveMega {
  const entry: ActiveMega = {
    formKey,
    level,
    activatedAt: now,
    expiresAt: now + MEGA_ACTIVE_MS,
  };
  const cur = readLevels();
  const prev = cur[formKey];
  const active = readActive();
  // Re-stating the level of the Mega already running (the Bonuses card's
  // pills) is not another evolution; a fresh activation is.
  const restating = active?.formKey === formKey && active.activatedAt === now;
  const nextLevel: MegaLevel = prev && prev.level > level ? prev.level : level;
  const next: MegaLevelEntry = { ...(prev ?? {}), level: nextLevel, updatedAt: now };
  if (!restating) {
    // Every activation is one Mega Evolution of the form. Without a count
    // yet, the level itself is the floor (Base means at least one).
    const base = prev?.evolutions ?? megaLevelInfo(nextLevel).evolutionsToReach ?? 1;
    next.evolutions = base + 1;
    delete next.progressToNext;
    next.lastActivatedAt = now;
    next.restEndsAt = now + megaLevelInfo(nextLevel).restHours * 3_600_000;
  }
  writeLevels({ ...cur, [formKey]: next });
  writeActive(entry);
  return entry;
}

export function clearActiveMega(): void {
  writeActive(null);
}

export function activeMegaForm(active: ActiveMega | null): MegaFormRef | null {
  return active ? megaFormByKey(active.formKey) : null;
}

/* ── React ───────────────────────────────────────────────────────────────── */

function subscribe(cb: () => void): () => void {
  window.addEventListener(MEGA_STORE_EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(MEGA_STORE_EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

export function useMegaLevels(): Readonly<LevelsShape> {
  return useSyncExternalStore(subscribe, readLevels, readLevels);
}

/** The active Mega, re-read every minute so it expires on the clock. */
export function useActiveMega(): ActiveMega | null {
  const stored = useSyncExternalStore(subscribe, readActive, () => null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!stored) return null;
  if (stored.expiresAt <= now) {
    // Expired since the last render: clear on the next tick of the loop so
    // this render stays pure, and report it gone now.
    queueMicrotask(() => getActiveMega(now));
    return null;
  }
  return stored;
}

/** Test seam. */
export function __resetMegaStoreForTests(): void {
  levelsCache = null;
  activeCache = undefined;
  try {
    window.localStorage.removeItem(LEVELS_KEY);
    window.localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* fine */
  }
}
